import { routeTranslations } from './routes'
import { DEFAULT_LOCALE, PREFIX_LOCALES, type Locale } from './locales'
import { normalizeLocale } from '@/utils/locale'
import { i18n } from '@/plugins/i18n'

type RouteKey = keyof typeof routeTranslations
export type RouteParams = Record<string, string | number>

/**
 * Replaces :param in the path template with actual values.
 */
function compilePath(template: string, params: RouteParams = {}) {
  return template.replace(/:([A-Za-z0-9_]+)/g, (_, key) => {
    const val = params[key]
    if (val === undefined) {
      throw new Error(`[i18nPath] Missing param "${key}" for template "${template}"`)
    }
    return encodeURIComponent(String(val))
  })
}

/**
 * Builds the correct, SEO-friendly path for both static and dynamic routes.
 */
export function localizedPath(
  key: RouteKey,
  params?: RouteParams,
  localeOverride?: Locale
): string {
  const activeLocale = normalizeLocale(String(i18n.global.locale.value || DEFAULT_LOCALE))
  const resolvedLocale =
    localeOverride ??
    activeLocale

  const locale = ((PREFIX_LOCALES as readonly string[]).includes(resolvedLocale)
    ? resolvedLocale
    : DEFAULT_LOCALE) as Locale

  const template = routeTranslations[key]?.[locale]

  if (template === undefined) {
    console.warn(`[i18nPath] Missing route for "${String(key)}" in locale "${locale}"`)
    return locale === DEFAULT_LOCALE ? '/' : `/${locale}`
  }

  const compiled = compilePath(template, params)

  // Default locale without a prefix
  if (locale === DEFAULT_LOCALE) {
    return compiled ? `/${compiled}` : '/'
  }

  // Prefixed locale
  return compiled ? `/${locale}/${compiled}` : `/${locale}`
}
